import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import RepositoryTree from './RepositoryTree';
import MonacoFileEditor from './MonacoFileEditor';
import Icon from './icons/Icon';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import HexagonalLoader from './HexagonalLoader';
import './CodeViewWithLogger.css';

/**
 * CodeViewWithLogger Component
 * 
 * Provides a comprehensive code editing experience with:
 * - File tree view (RepositoryTree)
 * - Monaco editor for editing
 * - IndexedDB-first approach for file storage
 * - Git pull/push integration for version control
 * - Project context management after git operations
 * - Deployment pipeline management
 * - Operation logging to show user what's happening
 * 
 * Workflow:
 * 1. On mount, try to load files from IndexedDB
 * 2. If no files in IndexedDB, pull from git and save to IndexedDB
 * 3. Show tree view and Monaco editor
 * 4. Listen for git pull/push events and update project context
 * 5. Log all operations to user
 * 
 * Exposed methods via ref:
 * - pullFromGit(): Pull latest changes from remote repository
 * - pushToGit(): Push local changes to remote repository
 * - triggerDeployment(): Trigger deployment pipeline
 */
const CodeViewWithLogger = forwardRef(({
  projectId,
  serviceName,
  repositoryUrl,
  branch = 'main',
  onFileSelect,
  onFileChange,
  onSave,
  initialFilePath = null,
  readOnly = false,
  isPreviewMode = false,
  // Project context for git operations
  selectedProject = null,
  selectedRevision = null,
  selectedTenant = null,
  selectedResource = null,
  configuration = null,
  collections = null,
  onProjectContextChange = null,
  // Preview refresh
  previewUrl = null,
  setPreviewUrl = null,
  setPreviewError = null,
  setForceGitFetch = null,
  autoTriggeredPullRef = null
}, ref) => {
  const [logs, setLogs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileObject, setSelectedFileObject] = useState(null); // Store full file object with metadata
  const [selectedLineNumber, setSelectedLineNumber] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [hasLoadedFromIndexedDB, setHasLoadedFromIndexedDB] = useState(false);
  const [fileTree, setFileTree] = useState([]);
  const [error, setError] = useState(null);
  const [logsExpanded, setLogsExpanded] = useState(false); // Default minimized
  const [fileErrors, setFileErrors] = useState({}); // Track file errors for tree view
  const [isPullingFromGit, setIsPullingFromGit] = useState(false); // Track git pull operation
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true); // Track auto-scroll state
  const initializingRef = useRef(false); // Prevent concurrent initializations
  const lastInitializedRef = useRef(null); // Track last initialized project+service
  const currentOpenFileRef = useRef(null);
  const originalConsoleRef = useRef(null); // Store original console methods

  // Track which file is currently open
  useEffect(() => {
    currentOpenFileRef.current = selectedFile || null;
  }, [selectedFile]);
  
  // Memoize the file object to prevent re-creation and loops
  const memoizedFileObject = useMemo(() => {
    if (selectedFileObject) {
      return selectedFileObject;
    }
    if (selectedFile) {
      return {
        path: selectedFile,
        name: selectedFile.split('/').pop(),
        content: fileContent,
        language: selectedFile.split('.').pop(),
        lineNumber: selectedLineNumber
      };
    }
    return null;
  }, [selectedFileObject, selectedFile, selectedLineNumber]); // Removed fileContent to prevent loop

  // Add log entry
  const addLog = useCallback((message, type = 'info', source = 'app') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      message,
      type, // 'info', 'success', 'error', 'warning'
      source // 'app', 'console', 'git'
    };
    setLogs(prev => [...prev, logEntry]);
    
    // Only log to console if not from console itself to avoid recursion
    if (source !== 'console' && originalConsoleRef.current) {
      originalConsoleRef.current.log(`[${type.toUpperCase()}] ${message}`);
    }
  }, []);

  // Auto-scroll logs to bottom (only when auto-scroll is enabled)
  useEffect(() => {
    if (isAutoScrollEnabled) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isAutoScrollEnabled]);

  // Handle scroll events to detect when user scrolls up/down
  const handleLogsScroll = useCallback(() => {
    if (!logsContainerRef.current) return;
    
    const container = logsContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Check if user is at the bottom (with a small threshold of 10px)
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
    
    // Enable auto-scroll when at bottom, disable when scrolled up
    setIsAutoScrollEnabled(isAtBottom);
  }, []);

  // Attach scroll listener to logs container
  useEffect(() => {
    const container = logsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleLogsScroll);
      return () => container.removeEventListener('scroll', handleLogsScroll);
    }
  }, [handleLogsScroll]);

  // Intercept console.log, console.error, console.warn to capture in Operation Log
  useEffect(() => {
    // Store original console methods
    if (!originalConsoleRef.current) {
      originalConsoleRef.current = {
        log: console.log,
        error: console.error,
        warn: console.warn,
        info: console.info
      };
    }

    const captureConsole = (method, type) => (...args) => {
      // Call original console method
      originalConsoleRef.current[method](...args);
      
      // Add to operation log - defer state update to avoid render phase warnings
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      // Use queueMicrotask to defer setState until after render
      queueMicrotask(() => {
        addLog(message, type, 'console');
      });
    };

    // Override console methods
    console.log = captureConsole('log', 'info');
    console.error = captureConsole('error', 'error');
    console.warn = captureConsole('warn', 'warning');
    console.info = captureConsole('info', 'info');

    // Cleanup: restore original console methods
    return () => {
      if (originalConsoleRef.current) {
        console.log = originalConsoleRef.current.log;
        console.error = originalConsoleRef.current.error;
        console.warn = originalConsoleRef.current.warn;
        console.info = originalConsoleRef.current.info;
      }
    };
  }, [addLog]);

  /**
   * Load files from IndexedDB
   * @returns {Promise<Array>} Array of files from IndexedDB
   */
  const loadFromIndexedDB = useCallback(async () => {
    if (!projectId || !serviceName) {
      addLog('⚠️ Missing project or service information', 'warning');
      return [];
    }

    try {
      addLog('🔍 Checking IndexedDB for cached files...', 'info');
      const files = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
      
      if (files && files.length > 0) {
        addLog(`✅ Found ${files.length} files in IndexedDB cache`, 'success');
        setHasLoadedFromIndexedDB(true);
        
        // Convert to tree format
        const treeData = files.map(file => ({
          path: file.filePath,
          type: 'blob',
          name: file.fileName || file.filePath.split('/').pop(),
          id: file.id,
          state: file.state,
          lastModified: file.lastModified
        }));
        
        setFileTree(treeData);
        return files;
      } else {
        addLog('📭 No files found in IndexedDB', 'info');
        return [];
      }
    } catch (error) {
      addLog(`❌ Error loading from IndexedDB: ${error.message}`, 'error');
      console.error('Error loading from IndexedDB:', error);
      return [];
    }
  }, [projectId, serviceName, addLog]);

  // Listen for Git operation events
  useEffect(() => {
    const handleGitPushRequested = () => {
      // Detect provider from service data
      const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
      const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
      const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
      addLog(`🚀 Push requested - sending changes to ${provider}...`, 'info', 'git');
    };

    const handleGitPushCompleted = (event) => {
      const {success, filesCount, error} = event.detail || {};
      if (success === false) {
        addLog(`❌ Push failed: ${error || 'Unknown error'}`, 'error', 'git');
      } else {
        addLog(`✅ Push completed successfully (${filesCount || 0} file(s))`, 'success', 'git');
      }
    };

    const handleGitPullRequested = () => {
      // Detect provider from service data
      const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
      const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
      const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
      addLog(`⬇️ Pull requested - fetching latest from ${provider}...`, 'info', 'git');
      setIsPullingFromGit(true);
    };

    const handleGitPullCompleted = async (event) => {
      const {success, error} = event.detail || {};
      if (success === false) {
        addLog(`❌ Pull failed: ${error || 'Unknown error'}`, 'error', 'git');
        setIsPullingFromGit(false);
      } else {
        // Detect provider from service data
        const service = JSON.parse(localStorage.getItem(`service_${projectId}_${serviceName}`) || '{}');
        const repoUrl = service.repositoryUrl || service.repoUrl || service.sshUrl;
        const provider = repoUrl?.includes('github.com') ? 'GitHub' : 'GitLab';
        addLog(`✅ Pull completed - files updated from ${provider}`, 'success', 'git');
        // Reload file tree from IndexedDB after pull completes
        addLog('🔄 Refreshing file tree...', 'info');
        const files = await loadFromIndexedDB();
        setIsPullingFromGit(false);
        
        // Log success with file count
        if (files && files.length > 0) {
          addLog(`✅ ${files.length} file(s) loaded successfully`, 'success');
        }
      }
    };

    window.addEventListener('gitPushRequested', handleGitPushRequested);
    window.addEventListener('gitPushCompleted', handleGitPushCompleted);
    window.addEventListener('gitPullRequested', handleGitPullRequested);
    window.addEventListener('gitPullCompleted', handleGitPullCompleted);

    return () => {
      window.removeEventListener('gitPushRequested', handleGitPushRequested);
      window.removeEventListener('gitPushCompleted', handleGitPushCompleted);
      window.removeEventListener('gitPullRequested', handleGitPullRequested);
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
    };
  }, [addLog, loadFromIndexedDB]);

  /**
   * Pull files from git repository and save to IndexedDB
   */
  const pullFromGit = useCallback(async () => {
    if (!repositoryUrl) {
      addLog('⚠️ No repository URL provided', 'warning');
      setError('No repository URL configured for this service');
      return;
    }

    if (!projectId || !serviceName) {
      addLog('⚠️ Missing project ID or service name', 'warning');
      return;
    }

    addLog('🔄 Dispatching pull request...', 'info');

    try {
      // Dispatch event - RepositoryTree will handle the actual pull
      window.dispatchEvent(new CustomEvent('gitPullRequested', {
        detail: {
          projectId,
          serviceName,
          branch
        }
      }));

      addLog('✅ Pull request dispatched', 'info');

    } catch (error) {
      addLog(`❌ Error pulling from git: ${error.message}`, 'error');
      // eslint-disable-next-line no-console
      console.error('Error pulling from git:', error);
      setError(error.message);
    }
  }, [repositoryUrl, projectId, serviceName, branch, addLog]);
  useEffect(() => {
    const initialize = async () => {
      // Prevent concurrent initializations
      if (initializingRef.current) {
        addLog('⏳ Already initializing, skipping...', 'info');
        return;
      }
      
      // Check if already initialized for this project+service
      const currentKey = `${projectId}:${serviceName}`;
      const isAlreadyInitialized = lastInitializedRef.current === currentKey;
      
      if (isAlreadyInitialized) {
        // Already initialized but refresh the file tree display in case files changed
        addLog('🔄 Refreshing file tree from IndexedDB...', 'info');
        await loadFromIndexedDB();
        return;
      }
      
      initializingRef.current = true;
      addLog('🚀 Initializing Code View...', 'info');
      
      try {
        // Try loading from IndexedDB first
        const cachedFiles = await loadFromIndexedDB();
        
        // If no cached files, pull from git
        if (cachedFiles.length === 0) {
          addLog('📥 No cached files found, pulling from Git...', 'info');
          await pullFromGit();
        } else {
          addLog('✨ Ready to edit!', 'success');
        }
        
        // Mark as successfully initialized
        lastInitializedRef.current = currentKey;
      } catch (error) {
        addLog(`❌ Initialization failed: ${error.message}`, 'error');
        console.error('Initialization error:', error);
      } finally {
        initializingRef.current = false;
      }
    };

    if (projectId && serviceName) {
      initialize();
    }
  }, [projectId, serviceName, addLog, loadFromIndexedDB, pullFromGit]); // Only run when project/service changes

  /**
   * Handle file selection from tree
   * RepositoryTree passes a file object with { path, name, etc }
   * 
   * OPTIMIZATION: Only loads the specific selected file from IndexedDB,
   * not the entire project context. This prevents the 12+ second freeze
   * when switching between files.
   */
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    // Extract filePath - RepositoryTree passes an object with path property
    const filePath = typeof file === 'string' ? file : file.path;
    const lineNumber = typeof file === 'object' ? file.lineNumber : null;
    
    // Debug logging
    // eslint-disable-next-line no-console
    console.log('🔍 CodeViewWithLogger handleFileSelect received:', {file, filePath, lineNumber});
    
    if (!filePath) return;

    setIsLoadingFile(true);
    setSelectedFile(filePath);
    setSelectedLineNumber(lineNumber);
    addLog(`📂 Opening file: ${filePath}${lineNumber ? ` (line ${lineNumber})` : ''}`, 'info');

    try {
      // Load ONLY this specific file from IndexedDB - don't reload entire project
      const fileData = await indexedDbFileStorage.getFile(projectId, serviceName, filePath);
      
      if (fileData) {
        // Detect if file is binary based on extension (fallback if isBinary flag not set)
        // SVG files are text-based XML, not binary - treat them as editable text
        const isImageFile = /\.(png|jpg|jpeg|gif|bmp|ico|webp|tiff|tif)$/i.test(filePath);
        const isSvg = /\.svg$/i.test(filePath);
        const isBinary = fileData.isBinary || isImageFile; // SVG excluded from binary
        
        // Determine MIME type - normalize extensions
        let mimeType = fileData.mimeType;
        if (!mimeType && (isImageFile || isSvg)) {
          const ext = filePath.split('.').pop().toLowerCase();
          const mimeTypeMap = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'ico': 'image/x-icon',
            'svg': 'image/svg+xml',
            'webp': 'image/webp',
            'tiff': 'image/tiff',
            'tif': 'image/tiff'
          };
          mimeType = mimeTypeMap[ext] || 'application/octet-stream';
        }
        
        // For binary images (not SVG), create blob URL for preview
        let binaryUrl = null;
        if (isBinary && mimeType?.startsWith('image/') && !isSvg) {
          // eslint-disable-next-line no-console
          console.log('🖼️ Creating blob URL for image:', filePath, {isBinary, mimeType});
          binaryUrl = await indexedDbFileStorage.getFileAsUrl(projectId, serviceName, filePath);
          // eslint-disable-next-line no-console
          console.log('🔗 Blob URL created:', binaryUrl ? 'SUCCESS' : 'FAILED', binaryUrl?.substring(0, 50));
        }
        
        // Create full file object with all metadata
        const fileWithMetadata = {
          ...fileData,
          path: filePath,
          name: filePath.split('/').pop(),
          lineNumber,
          isBinary,
          mimeType,
          binaryUrl
        };
        
        setFileContent(fileData.content);
        setSelectedFileObject(fileWithMetadata); // Store full file object
        addLog(`✅ Loaded ${filePath} (${fileData.state})`, 'success');
        
        // Call onFileSelect callback if provided
        if (onFileSelect) {
          // eslint-disable-next-line no-console
          console.log('📤 Sending file to editor:', {path: filePath, isBinary, mimeType, hasBinaryUrl: !!binaryUrl});
          onFileSelect(filePath, fileWithMetadata);
        }
      } else {
        addLog(`⚠️ File not found: ${filePath}`, 'warning');
        setFileContent('');
      }
    } catch (error) {
      addLog(`❌ Error loading file: ${error.message}`, 'error');
      setFileContent('');
    } finally {
      setIsLoadingFile(false);
    }
  }, [projectId, serviceName, addLog, onFileSelect]);

  /**
   * Handle file content changes from Monaco
   * 
   * OPTIMIZATION: Save file without triggering full project reload.
   * Only refresh the file tree metadata, not all file contents.
   */
  const handleFileChange = useCallback(async (filePath, newContent) => {
    if (!filePath) return;

    try {
      addLog(`💾 Saving changes to ${filePath}...`, 'info');
      
      // Save to IndexedDB
      await indexedDbFileStorage.saveFile(
        projectId,
        serviceName,
        filePath,
        newContent,
        { branch }
      );

      addLog(`✅ Saved ${filePath}`, 'success');
      
      if (onFileChange) {
        onFileChange(filePath, newContent);
      }

      // Refresh file tree to show modified state (but only tree metadata, not all content)
      // This is much faster than loading all file contents
      await loadFromIndexedDB();
    } catch (error) {
      addLog(`❌ Error saving file: ${error.message}`, 'error');
    }
  }, [projectId, serviceName, branch, addLog, onFileChange, loadFromIndexedDB]);

  /**
   * Handle manual refresh from git
   */
  const handleRefresh = useCallback(async () => {
    addLog('🔄 Refreshing from Git...', 'info');
    await pullFromGit();
  }, [addLog, pullFromGit]);

  /**
   * Handle push to git
   */
  const pushToGit = useCallback(async () => {
    addLog('🚀 Pushing to Git...', 'info');
    try {
      // Get modified files from IndexedDB
      const modifiedFiles = await indexedDbFileStorage.getModifiedFiles(projectId, serviceName);
      
      if (modifiedFiles.length === 0) {
        addLog('⚠️ No modified files to push', 'warning');
        return;
      }
      
      addLog(`📤 Pushing ${modifiedFiles.length} modified file(s)...`, 'info');
      
      // Dispatch event for push operation
      window.dispatchEvent(new CustomEvent('gitPushRequested', {
        detail: {
          projectId,
          serviceName,
          files: modifiedFiles
        }
      }));
      
      addLog('✅ Push completed successfully', 'success');
    } catch (error) {
      addLog(`❌ Push failed: ${error.message}`, 'error');
      throw error;
    }
  }, [projectId, serviceName, addLog]);

  /**
   * Listen for git pull completed events and update project context
   */
  useEffect(() => {
    const handleGitPullCompleted = async (event) => {
      const {projectId: pulledProjectId, serviceName: pulledServiceName} = event.detail || {};
      
      // Only handle if this is for our current service
      if (pulledProjectId !== projectId || pulledServiceName !== serviceName) {
        return;
      }
      
      addLog('✅ Pull completed successfully', 'success');
      
      try {
        // Refresh project context with newly pulled files from IndexedDB
        const filesArray = await indexedDbFileStorage.getAllFiles(projectId, serviceName);
        const allFiles = await Promise.all(filesArray.map(async (fileObj) => {
          return {
            path: fileObj.filePath,
            name: fileObj.fileName,
            content: fileObj.content || '',
            isNew: fileObj.state === 'new',
            modified: fileObj.state !== 'original'
          };
        }));
        
        addLog(`📂 Refreshed ${allFiles.length} files from IndexedDB`, 'info');
        
        // Clear auto-trigger flag if we have files
        if (allFiles.length > 0 && autoTriggeredPullRef?.current) {
          const serviceKey = `${projectId}|${serviceName}`;
          autoTriggeredPullRef.current.delete(serviceKey);
        }
        
        // Update project context via callback
        if (onProjectContextChange && selectedProject && selectedResource) {
          // Use internal selectedFile state for context
          const currentFileData = selectedFile ? {
            name: selectedFile.split('/').pop(),
            path: selectedFile,
            content: (await indexedDbFileStorage.getFile(
              projectId,
              serviceName,
              selectedFile
            ))?.content || '',
            language: selectedFile.split('.').pop()
          } : null;
          
          onProjectContextChange({
            tenant: selectedTenant,
            project: selectedProject,
            revision: selectedRevision,
            selectedResource: selectedResource,
            selectedMicroservice: {
              name: serviceName,
              type: selectedResource.data?.type,
              repositoryUrl: repositoryUrl
            },
            selectedFile: currentFileData,
            allFiles: allFiles,
            configuration: configuration,
            collections: collections,
            projectId: projectId
          });
        }
        
        // Refresh preview if applicable
        if (previewUrl && selectedResource && selectedResource.data?.name === pulledServiceName) {
          if (setPreviewUrl) setPreviewUrl(null);
          if (setPreviewError) setPreviewError(null);
          if (setForceGitFetch) setForceGitFetch(true);
          
          addLog('🔄 Refreshing preview...', 'info');
          
          // Small delay to ensure IndexedDB is fully updated
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Reload files to update tree
        await loadFromIndexedDB();
      } catch (error) {
        addLog(`❌ Failed to refresh after pull: ${error.message}`, 'error');
        console.error('Error in gitPullCompleted handler:', error);
      }
    };

    window.addEventListener('gitPullCompleted', handleGitPullCompleted);
    
    return () => {
      window.removeEventListener('gitPullCompleted', handleGitPullCompleted);
    };
  }, [projectId, serviceName, selectedProject, selectedRevision, selectedTenant, selectedResource, repositoryUrl, configuration, collections, previewUrl, onProjectContextChange, setPreviewUrl, setPreviewError, setForceGitFetch, autoTriggeredPullRef, addLog, loadFromIndexedDB]);

  // Listen for Git repository clone completion
  useEffect(() => {
    const handleGitCloneCompleted = async (event) => {
      const { owner, repo, clone_path, files_count } = event.detail;
      
      addLog(`✅ Git repository ${owner}/${repo} cloned successfully`, 'success');
      addLog(`📂 Location: ${clone_path}`, 'info');
      addLog(`📊 Files: ${files_count} (indexing in progress...)`, 'info');
    };
    
    const handleGitCloneIndexed = async (event) => {
      const { repo, indexed_count } = event.detail;
      
      addLog(`✅ Repository ${repo} indexed: ${indexed_count} files`, 'success');
      addLog(`📂 Files available under: source-code-for-analysis/${repo}/`, 'info');
      
      // Refresh file tree to show cloned files
      try {
        await loadFromIndexedDB();
        addLog('🔄 File tree refreshed', 'success');
      } catch (error) {
        addLog(`⚠️ Could not refresh file tree: ${error.message}`, 'warning');
      }
    };

    window.addEventListener('git_clone_completed', handleGitCloneCompleted);
    window.addEventListener('git_clone_indexed', handleGitCloneIndexed);
    
    return () => {
      window.removeEventListener('git_clone_completed', handleGitCloneCompleted);
      window.removeEventListener('git_clone_indexed', handleGitCloneIndexed);
    };
  }, [addLog, loadFromIndexedDB]);

  /**
   * Clear logs
   */
  const handleClearLogs = useCallback(() => {
    setLogs([]);
    addLog('🗑️ Logs cleared', 'info');
  }, [addLog]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    pullFromGit: async () => {
      addLog('🔄 Pull triggered from toolbar', 'info');
      await pullFromGit();
    },
    pushToGit: async () => {
      await pushToGit();
    },
    refresh: async () => {
      await handleRefresh();
    },
    triggerDeployment: async (environment, revisionId) => {
      if (!selectedProject?.id || !environment) {
        addLog('❌ Missing project or environment for deployment', 'error');
        throw new Error('Missing required deployment parameters');
      }
      
      addLog(`🚀 Deploying to ${environment.envId}...`, 'info');
      
      try {
        const result = await miaPlatformDataService.triggerDeployment(selectedProject.id, {
          deployType: 'deploy_all',
          environment: environment.envId,
          refType: 'revision',
          revision: revisionId
        });
        
        const pipelineId = result.pipelineId || result.id;
        
        if (!pipelineId) {
          throw new Error('Invalid deployment response: missing pipeline ID');
        }
        
        addLog(`✅ Deployment triggered (Pipeline: ${pipelineId})`, 'success');
        
        return { pipelineId, result };
      } catch (error) {
        addLog(`❌ Deployment failed: ${error.message}`, 'error');
        throw error;
      }
    },
    getPipelineStatus: async (pipelineId, environmentId) => {
      if (!selectedProject?.id || !pipelineId) {
        throw new Error('Missing project or pipeline ID');
      }
      
      try {
        const status = await miaPlatformDataService.getPipelineStatus(
          selectedProject.id,
          pipelineId,
          environmentId
        );
        
        return status;
      } catch (error) {
        addLog(`❌ Failed to fetch pipeline status: ${error.message}`, 'error');
        throw error;
      }
    }
  }), [pullFromGit, pushToGit, handleRefresh, addLog, selectedProject, miaPlatformDataService]);

  // Handle errors from Monaco editor
  const handleEditorErrors = useCallback((filePath, errors) => {
    setFileErrors(prev => {
      if (errors.length === 0) {
        // Only clear if this is the currently open file
        if (filePath === currentOpenFileRef.current) {
          const {[filePath]: removed, ...rest} = prev;
          return rest;
        }
        return prev;
      }
      // Update errors for this file
      return {...prev, [filePath]: errors};
    });
  }, []);

  // Handle "Ask AI to Fix" from tree view
  const handleAskAIToFix = useCallback((filePath, fileContent, errorDetails) => {
    // Dispatch event to communicate with App component
    window.dispatchEvent(new CustomEvent('askAIFromFile', {
      detail: {
        filePath,
        fileContent,
        errorDetails
      }
    }));
  }, []);

  // Load initial file if specified
  useEffect(() => {
    if (initialFilePath && hasLoadedFromIndexedDB) {
      handleFileSelect(initialFilePath);
    }
  }, [initialFilePath, hasLoadedFromIndexedDB, handleFileSelect]);

  if (!projectId || !serviceName) {
    return (
      <div className="code-view-with-logger error">
        <div className="error-message">
          <Icon name="warning" size={24} />
          <p>Missing project or service information</p>
        </div>
      </div>
    );
  }

  return (
    <div className="code-view-with-logger">
      {/* Main content area */}
      <div className="code-view-main">
        {/* Left: File tree */}
        <div className="code-view-sidebar">
          <div className="sidebar-content">
            {isLoadingFiles ? (
              <div className="loading-state">
                <Icon name="loading" size={24} />
                <p>Loading files...</p>
              </div>
            ) : (
              <>
                {/* Always render RepositoryTree so it can listen for git events */}
                <RepositoryTree
                  repositoryUrl={repositoryUrl}
                  onFileSelect={handleFileSelect}
                  selectedFilePath={selectedFile || ''}
                  serviceName={serviceName}
                  projectId={projectId}
                  fileErrors={fileErrors}
                  onAskAIToFix={handleAskAIToFix}
                  isPullingFromGit={isPullingFromGit}
                  tenantId={selectedTenant?.tenantId || selectedTenant?.id}
                  branchName={branch}
                />
                {/* Show empty state overlay when no files */}
                {fileTree.length === 0 && (
                  <div className="empty-state">
                    <Icon name="folder" size={48} />
                    <p>No files available</p>
                    {error && <p className="error-text">{error}</p>}
                    {isPullingFromGit ? (
                      <div className="pulling-state">
                        <Icon name="loading" size={20} />
                        <span>Pulling...</span>
                      </div>
                    ) : (
                      <button
                        className="btn-pull"
                        onClick={handleRefresh}
                        type="button"
                      >
                        Pull from Git
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right: Monaco editor */}
        <div className="code-view-editor">
          {isPullingFromGit && fileTree.length === 0 ? (
            <div className="loading-state">
              <HexagonalLoader message="Downloading code from Git..." />
            </div>
          ) : selectedFile ? (
            isLoadingFile ? (
              <div className="loading-state">
                <Icon name="loading" size={24} />
                <p>Loading file...</p>
              </div>
            ) : (
              <MonacoFileEditor
                file={memoizedFileObject}
                onFileChange={(content) => handleFileChange(selectedFile, content)}
                onErrorsChange={handleEditorErrors}
                onSave={onSave}
                isPreviewMode={isPreviewMode}
                projectId={projectId}
                serviceName={serviceName}
                readOnly={readOnly}
              />
            )
          ) : (
            <div className="empty-state">
              <Icon name="file" size={48} />
              <p>Select a file to edit</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Operation logs */}
      <div className={`code-view-logs ${logsExpanded ? 'expanded' : 'minimized'}`}>
        <div className="logs-header" onClick={() => setLogsExpanded(!logsExpanded)}>
          <div className="logs-header-left">
            <h4>
              <Icon name="terminal" size={16} />
              Operation Log
              {!logsExpanded && logs.length > 0 && (
                <span className="log-count-badge">{logs.length}</span>
              )}
            </h4>
            {!logsExpanded && logs.length > 0 && (
              <span className="last-log-preview">
                <span className={`log-icon log-icon-${logs[logs.length - 1].type}`}>
                  {logs[logs.length - 1].type === 'error' ? '❌' : 
                   logs[logs.length - 1].type === 'warning' ? '⚠️' :
                   logs[logs.length - 1].type === 'success' ? '✅' : 'ℹ️'}
                </span>
                {logs[logs.length - 1].message}
              </span>
            )}
          </div>
          <div className="logs-header-actions">
            {logs.length > 0 && (
              <button
                className="btn-clear-logs"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearLogs();
                }}
                type="button"
                title="Clear logs"
              >
                <Icon name="trash" size={14} />
              </button>
            )}
            <button
              className="btn-toggle-logs"
              type="button"
              title={logsExpanded ? 'Minimize logs' : 'Expand logs'}
              onClick={(e) => {
                e.stopPropagation();
                setLogsExpanded(!logsExpanded);
              }}
            >
              <Icon name={logsExpanded ? 'chevron-down' : 'chevron-up'} size={16} />
            </button>
          </div>
        </div>
        <div className="logs-content">
          {logs.length === 0 ? (
            <div className="logs-empty">
              <p>No operations logged yet</p>
            </div>
          ) : (
            <div className="logs-list" ref={logsContainerRef}>
              {logs.map(log => (
                <div key={log.id} className={`log-entry log-${log.type}`}>
                  <span className="log-timestamp">{log.timestamp}</span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

CodeViewWithLogger.displayName = 'CodeViewWithLogger';

CodeViewWithLogger.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  repositoryUrl: PropTypes.string,
  branch: PropTypes.string,
  onFileSelect: PropTypes.func,
  onFileChange: PropTypes.func,
  onSave: PropTypes.func,
  initialFilePath: PropTypes.string,
  readOnly: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  // Project context
  selectedProject: PropTypes.object,
  selectedRevision: PropTypes.object,
  selectedTenant: PropTypes.object,
  selectedResource: PropTypes.object,
  configuration: PropTypes.object,
  collections: PropTypes.object,
  onProjectContextChange: PropTypes.func,
  // Preview control
  previewUrl: PropTypes.string,
  setPreviewUrl: PropTypes.func,
  setPreviewError: PropTypes.func,
  setForceGitFetch: PropTypes.func,
  autoTriggeredPullRef: PropTypes.object
};

export default CodeViewWithLogger;
